import { Shield, Mail, Phone, Building2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartSidebar from "@/components/CartSidebar";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  const lastUpdated = "December 30, 2024";

  return (
    <div className="min-h-screen">
      <Header />
      <CartSidebar />
      
      <main className="pt-20 pb-12 bg-background">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <Button variant="ghost" className="mb-6 gap-2" asChild>
              <Link to="/">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </Button>
            
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-2">
                  Privacy Policy
                </h1>
                <p className="text-muted-foreground">
                  Last updated: {lastUpdated}
                </p>
              </div>
            </div>
          </div>

          {/* Introduction */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                1. Introduction
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Welcome to Slate us ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By using our website and services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
              <div className="mt-6 p-4 rounded-lg bg-muted/50">
                <p className="text-sm font-semibold text-foreground mb-2">Company Information:</p>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p><strong>Company Name:</strong> Slate us</p>
                  <p><strong>Registered Address:</strong> South Delhi, India</p>
                  <p><strong>Email:</strong> <a href="mailto:support@slateus.com" className="text-primary hover:underline">support@slateus.com</a></p>
                  <p><strong>Phone:</strong> <a href="tel:+919024286490" className="text-primary hover:underline">+91 9024286490</a></p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                2. Information We Collect
              </h2>
              
              <h3 className="font-semibold text-foreground mb-3 mt-6">2.1 Personal Information</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We collect personal information that you provide directly to us when you:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li>Create an account or register for our services</li>
                <li>Make a booking or purchase a trip</li>
                <li>Contact us for customer support</li>
                <li>Subscribe to our newsletter or marketing communications</li>
                <li>Participate in surveys or promotions</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                This information may include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li>Name, email address, and phone number</li>
                <li>Date of birth (if required for travel eligibility)</li>
                <li>Payment information (processed securely through our payment gateway)</li>
                <li>Passport or travel document details (when required for bookings)</li>
                <li>Emergency contact information</li>
                <li>Travel preferences and special requirements</li>
              </ul>

              <h3 className="font-semibold text-foreground mb-3 mt-6">2.2 Booking Information</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We collect information related to your bookings, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li>Trip selections and booking history</li>
                <li>Travel dates and destinations</li>
                <li>Group size preferences</li>
                <li>Special requests or accommodations</li>
              </ul>

              <h3 className="font-semibold text-foreground mb-3 mt-6">2.3 Technical Information</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When you visit our website, we automatically collect certain technical information, including:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li>IP address and location data</li>
                <li>Browser type and version</li>
                <li>Device information (type, operating system)</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </CardContent>
          </Card>

          {/* How We Use Your Information */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                3. How We Use Your Information
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li><strong>Processing Bookings:</strong> To process and manage your trip bookings, payments, and reservations</li>
                <li><strong>Communication:</strong> To send booking confirmations, trip updates, and important notifications via email and SMS</li>
                <li><strong>Customer Support:</strong> To respond to your inquiries, provide customer service, and resolve issues</li>
                <li><strong>Service Improvement:</strong> To analyze usage patterns and improve our website, services, and user experience</li>
                <li><strong>Marketing:</strong> To send promotional materials, newsletters, and special offers (only with your consent)</li>
                <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes</li>
                <li><strong>Fraud Prevention:</strong> To detect, prevent, and address fraud, security issues, and other harmful activities</li>
                <li><strong>Account Management:</strong> To manage your account, verify your identity, and maintain your booking history</li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Sharing & Third-Party Services */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                4. Data Sharing & Third-Party Services
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We may share your information with trusted third-party service providers who assist us in operating our business and serving you:
              </p>
              
              <h3 className="font-semibold text-foreground mb-3 mt-6">4.1 Payment Processors</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use <strong>Razorpay</strong> to process payments. When you make a payment, your payment information is handled securely by Razorpay in accordance with their privacy policy. We do not store your full payment card details on our servers.
              </p>

              <h3 className="font-semibold text-foreground mb-3 mt-6">4.2 Communication Services</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use third-party services to send communications:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li><strong>Resend:</strong> For sending booking confirmation emails</li>
                <li><strong>Twilio:</strong> For sending SMS notifications</li>
              </ul>

              <h3 className="font-semibold text-foreground mb-3 mt-6">4.3 Data Storage</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use <strong>Supabase</strong> to store and manage your data securely. All data is encrypted and stored in compliance with industry security standards.
              </p>

              <h3 className="font-semibold text-foreground mb-3 mt-6">4.4 No Sale of Personal Data</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We do not sell, rent, or trade your personal information to third parties for their marketing purposes. We only share information as described in this policy or with your explicit consent.
              </p>

              <h3 className="font-semibold text-foreground mb-3 mt-6">4.5 Legal Requirements</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We may disclose your information if required by law, court order, or government regulation, or if we believe disclosure is necessary to protect our rights, property, or safety, or that of our users or others.
              </p>
            </CardContent>
          </Card>

          {/* Data Security */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                5. Data Security
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure socket layer (SSL) technology for secure connections</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication procedures</li>
                <li>Secure payment processing through PCI-compliant providers</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.
              </p>
            </CardContent>
          </Card>

          {/* Cookies & Tracking */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                6. Cookies & Tracking Technologies
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our website. Cookies are small data files stored on your device that help us:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li>Remember your preferences and settings</li>
                <li>Maintain your session and login status</li>
                <li>Improve website functionality and performance</li>
                <li>Understand how you use our website</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our website.
              </p>
            </CardContent>
          </Card>

          {/* User Rights */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                7. Your Rights
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You have certain rights regarding your personal information. You may:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal and contractual obligations)</li>
                <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
                <li><strong>Object:</strong> Object to certain processing of your information</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                To exercise these rights, please contact us at <a href="mailto:support@slateus.com" className="text-primary hover:underline">support@slateus.com</a>. We will respond to your request within a reasonable timeframe.
              </p>
            </CardContent>
          </Card>

          {/* Marketing Communications */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                8. Marketing Communications
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                With your consent, we may send you promotional emails, newsletters, and special offers about our trips and services. You can opt-out of receiving marketing communications at any time by:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li>Clicking the "unsubscribe" link in any marketing email</li>
                <li>Contacting us at <a href="mailto:support@slateus.com" className="text-primary hover:underline">support@slateus.com</a></li>
                <li>Updating your preferences in your account settings</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Please note that even if you opt-out of marketing communications, we may still send you transactional emails related to your bookings, account, or important service updates.
              </p>
            </CardContent>
          </Card>

          {/* Payment Information */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                9. Payment Information
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                All payment transactions are processed securely through Razorpay, a PCI-DSS compliant payment gateway. We do not store your full credit card or debit card details on our servers. Payment information is encrypted and handled in accordance with industry security standards.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                When you make a payment, you are redirected to Razorpay's secure payment page. Your payment details are processed directly by Razorpay and are subject to their privacy policy and terms of service.
              </p>
            </CardContent>
          </Card>

          {/* Children's Privacy */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                10. Children's Privacy
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our services are intended for individuals who are 18 years of age or older. We do not knowingly collect personal information from children under the age of 18. If you are a parent or guardian and believe that your child has provided us with personal information, please contact us immediately at <a href="mailto:support@slateus.com" className="text-primary hover:underline">support@slateus.com</a>.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If we become aware that we have collected personal information from a child under 18 without parental consent, we will take steps to delete such information from our servers.
              </p>
            </CardContent>
          </Card>

          {/* International Users & GDPR */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                11. International Users & GDPR Compliance
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Currently, our services are primarily focused on users in India. However, as we expand internationally, we are committed to complying with applicable data protection laws, including the General Data Protection Regulation (GDPR) for users in the European Union.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you are located outside India, please be aware that your information may be transferred to, stored, and processed in India where our servers are located. By using our services, you consent to the transfer of your information to India.
              </p>
            </CardContent>
          </Card>

          {/* Changes to Privacy Policy */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                12. Changes to This Privacy Policy
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
              </p>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground mb-4 ml-4">
                <li>Posting the updated policy on this page with a new "Last updated" date</li>
                <li>Sending an email notification to registered users</li>
                <li>Displaying a prominent notice on our website</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information. Your continued use of our services after any changes constitutes your acceptance of the updated policy.
              </p>
            </CardContent>
          </Card>

          {/* Third-Party Links */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                13. Third-Party Links
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Our website may contain links to third-party websites, services, or applications that are not owned or controlled by us. This Privacy Policy does not apply to such third-party sites. We are not responsible for the privacy practices or content of these external sites.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                We encourage you to review the privacy policies of any third-party sites you visit. Your interactions with these sites are subject to their own terms and privacy policies.
              </p>
            </CardContent>
          </Card>

          {/* Contact Us */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                14. Contact Us
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="mt-6 p-6 rounded-lg bg-muted/50">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <Building2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Slate us</p>
                      <p className="text-sm text-muted-foreground">South Delhi, India</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Email</p>
                      <a href="mailto:support@slateus.com" className="text-sm text-primary hover:underline">support@slateus.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-foreground">Phone</p>
                      <a href="tel:+919024286490" className="text-sm text-primary hover:underline">+91 9024286490</a>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed mt-6">
                We will respond to your inquiries within a reasonable timeframe, typically within 5-7 business days.
              </p>
            </CardContent>
          </Card>

          {/* Consent */}
          <Card className="border-0 shadow-card mb-8">
            <CardContent className="p-8">
              <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
                15. Consent
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                By using our website and services, you consent to the collection, use, and disclosure of your information as described in this Privacy Policy. If you do not agree with any part of this policy, please do not use our services.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                You may withdraw your consent at any time by contacting us at <a href="mailto:support@slateus.com" className="text-primary hover:underline">support@slateus.com</a>. However, withdrawing consent may affect your ability to use certain features of our services.
              </p>
            </CardContent>
          </Card>

          {/* Back to Home */}
          <div className="text-center mt-12">
            <Button size="lg" asChild>
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;

